import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import isEqual from 'lodash/isEqual';
import { useRouter } from 'next/router';

import { ENTITY_MAIN_TABS, VALIDATION_STATUS } from 'constants/index';

import { removeLanguageSuffixFromPath } from 'utils/globals/getRouteContext';
import usePermissions from 'utils/permissions';

import safeDecodeURL from 'server/utils/safeDecodeURL';

import { Router } from 'src/i18n';

import { ENTITY_TYPES, QUERY_PARAMS_RESERVED, ROUTE_RESERVED_KEYWORDS } from 'operabase-router/constants';
import LinkGenerator from 'operabase-router/handlers/LinkGenerator';

import useAppContext from './useAppContext';

const NavigateContext = createContext({});

const HISTORY_LIMIT = 100;

const NavigateProvider = ({ children }) => {
  const navigation = useRef([]);
  const router = useRouter();
  const { language, obRouteContext } = useAppContext();

  const { page, basePath, query } = obRouteContext || {};
  const { entity: pageEntity, entityType: pageEntityType } = page || {};
  const permissions = usePermissions(pageEntity?.id, pageEntityType);

  const scrollToSection = query?.[QUERY_PARAMS_RESERVED.SCROLL_TO];

  const linkGenerator = useMemo(() => new LinkGenerator({ language }), [language]);

  const scrollTo = useCallback(
    ({ top = 0, force = false, left = 0 } = {}) => {
      if (typeof window !== 'undefined' && (!scrollToSection || force)) {
        window.scrollTo({ top, left, behavior: 'instant' });
      }
    },
    [scrollToSection],
  );

  const removeLocaleFromPath = useCallback(path => removeLanguageSuffixFromPath(path, language), [language]);

  const updateQueryParams = useCallback(
    ({ deleteParams, appendParams, replaceExisting = false }) => {
      const [asPath, queryString] = Router.asPath.split('?');

      const currentQueryParams = new URLSearchParams(replaceExisting ? '' : queryString);

      if (deleteParams?.length > 0) {
        deleteParams.forEach(param => currentQueryParams.delete(param));
      }

      if (appendParams) {
        Object.entries(appendParams).forEach(([key, value]) => {
          if (value !== undefined) {
            currentQueryParams.set(key, value);
          }
        });
      }

      currentQueryParams.sort();
      const updateQueryString = safeDecodeURL(currentQueryParams.toString());
      const newAsPath = removeLocaleFromPath(asPath);

      Router.replace(
        {
          href: Router.pathname,
          query: Object.fromEntries(currentQueryParams),
        },
        `${newAsPath}${updateQueryString ? `?${updateQueryString}` : ''}`,
        { shallow: true },
      );
    },
    [removeLocaleFromPath],
  );

  const updatePageURL = useCallback((linkProps, shouldReplace = false) => {
    const action = shouldReplace ? 'replace' : 'push';

    if (linkProps?.href) {
      let path = linkProps?.as;

      if (!path && linkProps?.url) {
        path = linkProps?.url?.replace(/(?:\/\w{2})+$/g, '');
      }

      Router[action](linkProps.href, path, { shallow: linkProps.shallow ?? true });
    }
  }, []);

  const getLinkPropsWithPermissions = useCallback(
    args => {
      const { baseRoute, entityType, entity, edit, pro } = args || {};

      if (
        entityType === ENTITY_TYPES.PROFILE &&
        entity &&
        !edit &&
        entity?.validationStatus?.id !== VALIDATION_STATUS.HIDDEN
      ) {
        const hasCastingToolAccess = permissions?.castingToolPermissions?.hasAccess ?? false;
        const shouldSkipProPage =
          pro === false || (pro !== true && pageEntityType === ENTITY_TYPES.PROFILE && pageEntity?.id === entity?.id);
        const newBaseRoute =
          !shouldSkipProPage && hasCastingToolAccess && !baseRoute && pro !== false
            ? ROUTE_RESERVED_KEYWORDS.casting
            : baseRoute;

        return linkGenerator.getLinkProps({ ...args, baseRoute: newBaseRoute });
      }

      return linkGenerator.getLinkProps(args);
    },
    [linkGenerator, permissions, pageEntityType, pageEntity?.id],
  );

  const navigateToTab = useCallback(
    (tab, { entityType, entity, pro }) => {
      const linkProps = getLinkPropsWithPermissions({
        entityType,
        entity,
        pro,
        baseRoute: basePath,
        path: tab === ENTITY_MAIN_TABS.HOME ? undefined : tab,
      });

      updatePageURL(linkProps);
    },
    [basePath, getLinkPropsWithPermissions, updatePageURL],
  );

  const getNavigationState = () => navigation.current || [];

  const getHistoryIndex = useCallback(
    (native = true) => {
      const navigationState = getNavigationState();
      let lastIndex = navigationState.length - 1;

      if (!native) {
        const currentPathname = router.pathname;

        while (navigationState.length > 0 && navigationState?.[lastIndex]?.href === currentPathname) {
          lastIndex -= 1;
        }

        return lastIndex;
      }

      return lastIndex;
    },
    [router.pathname],
  );

  const getBackLinkProps = useCallback(
    (native = true, linkProps) => {
      const navigationState = getNavigationState();
      const historyIndex = getHistoryIndex(native);
      const shallow = native;

      const backLinkProps = navigationState[historyIndex];

      if (backLinkProps) {
        return {
          as: removeLocaleFromPath(backLinkProps.as),
          href: backLinkProps.href,
          shallow,
        };
      }

      return getLinkPropsWithPermissions({ shallow, onlyLinkProps: true, ...linkProps });
    },
    [getHistoryIndex, removeLocaleFromPath, getLinkPropsWithPermissions],
  );

  const navigateToHistory = useCallback(
    (native = true, linkProps) => {
      const navigationState = getNavigationState();
      const historyIndex = getHistoryIndex(native);
      const shallow = native;

      const finalHistory = navigationState.slice(0, historyIndex + 1);
      const finalHistoryLength = finalHistory?.length;

      if (finalHistoryLength > 0) {
        navigation.current = finalHistory;

        if (window) {
          window.history.go(historyIndex - (navigationState ? navigationState.length : 0));
        }
      } else {
        updatePageURL(getLinkPropsWithPermissions({ shallow, onlyLinkProps: true, ...linkProps }));
      }
    },
    [getHistoryIndex, updatePageURL, getLinkPropsWithPermissions],
  );

  const onRouteChange = useCallback(
    newPathname => {
      if (navigation.current) {
        const linkProps = {
          as: router.asPath,
          href: router.pathname,
        };

        const navigationState = getNavigationState();

        if (navigationState.length > 0) {
          const { as: lastAs } = navigationState[navigationState.length - 1];

          if (isEqual(lastAs, newPathname)) {
            navigationState.pop();
          } else {
            navigationState.push(linkProps);
          }
        } else {
          navigationState.push(linkProps);
        }

        navigation.current = navigationState.slice(-HISTORY_LIMIT);
      }
    },
    [router],
  );

  useEffect(() => {
    router.events.on('routeChangeStart', onRouteChange);

    return () => {
      router.events.off('routeChangeStart', onRouteChange);
    };
  }, [router, onRouteChange]);

  const navigate = useMemo(
    () => ({
      state: navigation.current,
      back: navigateToHistory,
      getBackLinkProps,
      to: updatePageURL,
      toTab: navigateToTab,
      getLinkProps: getLinkPropsWithPermissions,
      updateQueryParams,
      scrollTo,
      removeLocaleFromPath,
    }),
    [
      getBackLinkProps,
      getLinkPropsWithPermissions,
      navigateToHistory,
      navigateToTab,
      removeLocaleFromPath,
      scrollTo,
      updatePageURL,
      updateQueryParams,
    ],
  );

  return <NavigateContext.Provider value={navigate}>{children}</NavigateContext.Provider>;
};

const useNavigate = () => useContext(NavigateContext);

export { NavigateProvider };
export default useNavigate;
